"use client";

import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useState } from "react";
import type { Data as PlotlyData, Layout as PlotlyLayout } from "plotly.js";

const Plot = dynamic(() => import("react-plotly.js"), { ssr: false });

type RainPoint = { date: string; rain: number };
type WTDPoint = { datetime: string; value: number };
type Dipwell = { name: string; wtd: WTDPoint[] };

type VWBResp = {
  sy_avg: number;
  rain: RainPoint[];
  dipwells: Dipwell[];
};

type Payload = {
  site_name: string;
  start_date: string; // ISO (YYYY-MM-DD)
  end_date: string;   // ISO (YYYY-MM-DD)
};

type Props = {
  endpoint?: string;
  payload?: Payload;
  initial?: VWBResp;
  className?: string;
  height?: number;
  onLoaded?: (resp: VWBResp) => void;
};

export default function TimeseriesChart({
  endpoint,
  payload,
  initial,
  className,
  height = 420,
  onLoaded,
}: Props) {
  const [resp, setResp] = useState<VWBResp | null>(initial ?? null);
  const [loading, setLoading] = useState<boolean>(!initial && !!endpoint);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let abort = false;
    async function run() {
      if (initial) {
        setResp(initial);
        setLoading(false);
        setError(null);
        return;
      }
      if (!endpoint || !payload) {
        setLoading(false);
        setError("Missing endpoint/payload");
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${res.statusText} ${t}`.trim());
        }
        const json = (await res.json()) as VWBResp;
        if (abort) return;
        setResp(json);
        setLoading(false);
        onLoaded?.(json);
      } catch (e: any) {
        if (abort) return;
        console.error("TimeseriesChart fetch failed:", e);
        setLoading(false);
        setError(e?.message || "Failed to load time series.");
      }
    }
    run();
    return () => {
      abort = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, JSON.stringify(payload), initial]);

  // Helpers
  const toDate = (s: string) => new Date(s);

  // If WTD looks like meters (|value| < 10), convert to cm so the axis matches your spec.
  const shouldConvertToCm = useMemo(() => {
    if (!resp?.dipwells?.length) return false;
    const sample = resp.dipwells.flatMap(d => d.wtd.slice(0, 200)).map(p => Math.abs(p.value));
    if (!sample.length) return false;
    const max = Math.max(...sample);
    return max < 10; // likely meters -> convert to cm
  }, [resp]);

  const data = useMemo(() => {
    if (!resp) return [];

    // Dipwells (left axis)
    const wtdTraces = resp.dipwells.map((d, i) => ({
      type: "scatter" as const,
      mode: "lines",
      name: `${d.name} WTD`,
      x: d.wtd.map(p => toDate(p.datetime)),
      y: d.wtd.map(p => (shouldConvertToCm ? p.value * 100 : p.value)),
      line: { width: 1.8 },
      hovertemplate: "%{y:.2f}<extra>" + `${d.name} WTD` + "</extra>",
    }));

    // Rainfall (right axis)
    const rainTrace = {
      type: "scatter" as const,
      mode: "markers",
      name: "Precipitation",
      x: resp.rain.map(r => toDate(r.date)),
      y: resp.rain.map(r => r.rain),
      yaxis: "y2",
      marker: { size: 6, opacity: 0.7 },
      hovertemplate: "%{y:.2f} mm<extra>Precipitation</extra>",
    };

    return [...wtdTraces, rainTrace];
  }, [resp, shouldConvertToCm]);

  const layout = useMemo(() => {
    const start =
      payload?.start_date ?? (resp?.rain?.[0]?.date || resp?.dipwells?.[0]?.wtd?.[0]?.datetime);
    const end =
      payload?.end_date ??
      (resp?.rain?.slice(-1)?.[0]?.date ||
        resp?.dipwells?.[0]?.wtd?.slice(-1)?.[0]?.datetime);

    const title =
      resp && start && end
        ? `Specific Yield from ${new Date(start).toISOString().slice(0, 10)} to ${new Date(end)
            .toISOString()
            .slice(0, 10)}, ${resp.sy_avg.toFixed(3)}`
        : "Specific Yield";

    return {
      title: { text: title },
      autosize: true,
      height,
      margin: { l: 70, r: 70, t: 60, b: 50 },
      paper_bgcolor: "#ffffff",
      plot_bgcolor: "#ffffff",
      showlegend: true,
      hovermode: "x unified" as const,
      xaxis: {
        title: "Date",
        type: "date",
        showgrid: true,
        gridcolor: "rgba(0,0,0,0.1)",
      },
      yaxis: {
        title: `Water Table Depth (WTD, ${shouldConvertToCm ? "cm" : "m"})`,
        showgrid: true,
        gridcolor: "rgba(0,0,0,0.1)",
        zeroline: false,
      },
      yaxis2: {
        title: "Precipitation (mm)",
        overlaying: "y",
        side: "right",
        zeroline: false,
      },
    };
  }, [resp, height, payload, shouldConvertToCm]);

  if (loading) {
    return (
      <div
        className={className}
        style={{
          height,
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          background: "#fff",
          color: "#6b7280",
        }}
      >
        Loading time series…
      </div>
    );
  }

  if (error || !resp) {
    return (
      <div
        className={className}
        style={{
          height,
          border: "1px solid #fecaca",
          background: "#fff1f2",
          color: "#991b1b",
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          padding: 16,
          textAlign: "center",
        }}
      >
        Couldn’t load time series{error ? `: ${error}` : ""}.
      </div>
    );
  }

  return (
    <div className={className} style={{ background: "#fff", borderRadius: 12 }}>
      <Plot
        data={data as any}
        layout={layout as any}
        useResizeHandler
        style={{ width: "100%", height }}
        config={{
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: [
            "zoom2d",
            "pan2d",
            "select2d",
            "lasso2d",
            "zoomIn2d",
            "zoomOut2d",
            "autoScale2d", 
            "hoverClosestCartesian",
            "hoverCompareCartesian",
            "toggleSpikelines",
            "toggleHover",
            "sendDataToCloud",
          ],
        }}
      />
    </div>
  );
}